import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dog, CalendarClock, ClipboardList, CheckCircle2, GraduationCap, Sparkles,
  ArrowRight, Clock, MapPin, User, PawPrint,
} from "lucide-react";

const KPIS = [
  { k: "my_dogs", label: "My Dogs", icon: Dog },
  { k: "upcoming_classes", label: "Upcoming Classes", icon: CalendarClock },
  { k: "homework_assigned", label: "Homework To Do", icon: ClipboardList },
  { k: "homework_completed", label: "Homework Done", icon: CheckCircle2 },
];

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/client/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);
  const k = data?.kpis || {};

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {KPIS.map((kpi) => (
          <Card key={kpi.k} className="border-border bg-card" data-testid={`client-kpi-${kpi.k}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <kpi.icon size={15} strokeWidth={1.5} />
                <span className="text-[11px] uppercase tracking-wide font-semibold">{kpi.label}</span>
              </div>
              <div className="text-3xl font-heading font-semibold tracking-tight">{data ? k[kpi.k] : "—"}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Level + next recommended */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="border-border bg-secondary/40">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground flex items-center gap-1.5"><GraduationCap size={14} /> Current Training Level</div>
              <div className="mt-2">{data ? <StatusBadge value={k.current_level} /> : "—"}</div>
            </div>
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center"><PawPrint size={26} className="text-primary" strokeWidth={1.5} /></div>
          </CardContent>
        </Card>
        <Card className="border-border border-dashed">
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground flex items-center gap-1.5"><Sparkles size={14} /> Next Recommended Class</div>
              <div className="mt-1.5 text-sm text-muted-foreground">Smart recommendations arrive with HV Academy — coming soon.</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Dogs */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2"><Dog size={18} strokeWidth={1.5} /> My Dogs</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(data?.dogs || []).map((d) => (
            <Card key={d.id} className="border-border hover:-translate-y-0.5 hover:shadow-md transition-all cursor-pointer" onClick={() => navigate(`/dogs/${d.id}`)} data-testid="client-dog-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading font-semibold text-lg">{d.name}</div>
                    <div className="text-sm text-muted-foreground">{d.breed || "—"} · {d.age || "—"}</div>
                  </div>
                  <StatusBadge value={d.current_level} />
                </div>
                <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5"><GraduationCap size={13} /> {d.current_program || "No active program"}</div>
                  <div className="flex items-center gap-1.5"><User size={13} /> {d.trainer_name || "Unassigned"}</div>
                </div>
                <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <StatusBadge value={d.progress_status} />
                    {d.homework_status === "open" && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 border border-blue-200">Homework due</span>}
                  </div>
                  <ArrowRight size={16} className="text-muted-foreground" />
                </div>
                {d.next_class && (
                  <div className="mt-3 text-xs bg-muted/50 rounded-lg p-2.5">
                    <div className="text-muted-foreground uppercase tracking-wide text-[10px] font-semibold mb-1">Next class</div>
                    <div className="font-medium">{d.next_class.name}</div>
                    <div className="text-muted-foreground">{fmtDate(d.next_class.date)} · {d.next_class.time}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {data && data.dogs.length === 0 && <p className="text-muted-foreground col-span-full text-center py-12">No dogs yet.</p>}
        </div>
      </div>

      {/* Upcoming + homework */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2"><CalendarClock size={18} strokeWidth={1.5} /> Upcoming Classes</CardTitle>
            <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={() => navigate("/bookings")} data-testid="client-view-bookings">All <ArrowRight size={14} /></Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {data?.upcoming?.length ? data.upcoming.map((c) => (
              <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60 hover:bg-muted/40 transition-colors cursor-pointer" onClick={() => navigate(`/classes/${c.id}`)} data-testid="client-upcoming-class">
                <div>
                  <div className="font-medium text-sm">{c.name}</div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span className="flex items-center gap-1"><MapPin size={11} /> {c.location_name}</span>
                    <span className="flex items-center gap-1"><User size={11} /> {c.trainer_name}</span>
                  </div>
                </div>
                <div className="text-right"><div className="text-sm font-medium">{fmtDate(c.date)}</div><div className="text-xs text-muted-foreground flex items-center gap-1 justify-end"><Clock size={11} /> {c.time}</div></div>
              </div>
            )) : <p className="text-sm text-muted-foreground py-6 text-center">No upcoming classes.</p>}
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2"><ClipboardList size={18} strokeWidth={1.5} /> Homework</CardTitle>
            <Button variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={() => navigate("/homework")} data-testid="client-view-homework">All <ArrowRight size={14} /></Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {data?.recent_homework?.length ? data.recent_homework.map((h) => (
              <div key={h.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60" data-testid="client-homework-preview">
                <div><div className="text-sm font-medium">{h.title}</div><div className="text-xs text-muted-foreground">{h.dog_name} · Due {fmtDate(h.due_date)}</div></div>
                <StatusBadge value={h.status} />
              </div>
            )) : <p className="text-sm text-muted-foreground py-6 text-center">No homework yet.</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
